import { Button, Form, Input, Select } from 'antd';
import * as Component from 'components';
import { COMPANY_COUNTRIES, DEFAULT_COMPANY_COUNTRY } from 'constants/index';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { companyApiService } from 'service/api/companyApiService';
import { getContentProviderEditHandlers } from '../handlers/ContentProviderEditHandlers';

const { Option } = Select;

const ContentProviderEdit: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { id } = useParams();
  const { onFinish } = getContentProviderEditHandlers({
    navigate,
    form,
    id: id || '',
  });

  useEffect(() => {
    const fetchCompany = async (): Promise<void> => {
      const company = await companyApiService.fetchById(Number(id));
      form.setFieldsValue(company);
      if (!company.country) {
        form.setFieldsValue({ country: DEFAULT_COMPANY_COUNTRY });
      }
    };
    fetchCompany().then();
  }, [id]);

  // Country search filter function (searchable by Korean or English name)
  const filterCountryOption = (
    input: string,
    option: { label?: string; labelEn?: string; value?: string } | undefined
  ) => {
    const searchTerm = input.toLowerCase();
    const label = option?.label?.toLowerCase() || '';
    const labelEn = option?.labelEn?.toLowerCase() || '';
    const value = option?.value?.toLowerCase() || '';

    // Search by Korean name, English name, or country code
    return label.includes(searchTerm) || labelEn.includes(searchTerm) || value.includes(searchTerm);
  };

  return (
    <Component.BasePageComponent>
      <Form form={form} onFinish={onFinish} layout="vertical">
        <Form.Item
          name="title"
          label="CP Origin"
          rules={[{ required: true, message: 'Please enter CP Origin' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="title_en"
          label="CP EN"
          rules={[{ required: true, message: 'Please enter CP EN' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input />
        </Form.Item>
        <Form.Item
          name="slug"
          label="Slug"
          rules={[
            { required: true, message: 'Please enter a slug' },
            {
              pattern: /^[a-z0-9_]+$/,
              message: 'Slug must contain only lowercase letters, numbers, and underscores',
            },
            {
              validator: (_, value) => {
                if (value && value.includes(' ')) {
                  return Promise.reject(new Error('Slug cannot contain spaces'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="country"
          label="Country"
          rules={[{ required: true, message: 'Please select a country' }]}
        >
          <Select
            showSearch
            placeholder="Select a country"
            filterOption={filterCountryOption}
            optionFilterProp="children"
            style={{ width: '100%' }}
          >
            {COMPANY_COUNTRIES.map(country => (
              <Option
                key={country.value}
                value={country.value}
                label={country.label}
                labelEn={country.labelEn}
              >
                {country.value} - {country.label} ({country.labelEn})
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="person_in_charge" label="ODK PIC">
          <Input />
        </Form.Item>
        <Form.Item
          name="person_in_charge_email_address"
          label="ODK PIC email address"
          rules={[
            {
              type: 'email',
              message: 'Please enter a valid email address',
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Edit
          </Button>
        </Form.Item>
      </Form>
    </Component.BasePageComponent>
  );
};

export default observer(ContentProviderEdit);
